import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import { useAuth } from "../../../context/AuthContext";
import {
  FiTerminal,
  FiRefreshCw,
  FiTrash2,
  FiDownload,
  FiSearch,
  FiAlertTriangle,
  FiCheckCircle,
  FiInfo,
  FiXCircle,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiCopy,
  FiCheck,
  FiPlay,
  FiZap,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiRotateCcw,
} from "react-icons/fi";

const SystemLogs = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Filters & Pagination State
  const [searchTerm, setSearchTerm] = useState("");
  const [localSearch, setLocalSearch] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    currentPage: 1,
    limit: 15,
  });

  const [stats, setStats] = useState({
    totalLogs: 0,
    errors: 0,
    warnings: 0,
    info: 0,
    success: 0,
  });

  const [actionFeedback, setActionFeedback] = useState(null);
  const autoRefreshTimerRef = useRef(null);

  // Fetch Logs from Backend
  const fetchLogs = useCallback(
    async ({
      page = pagination.currentPage,
      search = searchTerm,
      level = selectedLevel,
      category = selectedCategory,
      isBackground = false,
    } = {}) => {
      try {
        if (!isBackground) setRefreshing(true);
        const queryParams = new URLSearchParams();
        queryParams.append("page", page);
        queryParams.append("limit", 15);
        if (search && search.trim()) queryParams.append("search", search.trim());
        if (level && level !== "all") queryParams.append("level", level);
        if (category && category !== "all") queryParams.append("category", category);

        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE}/student/system-logs?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (res.data && res.data.success) {
          setLogs(res.data.data || []);
          if (res.data.pagination) {
            setPagination(res.data.pagination);
          }
          if (res.data.stats) {
            setStats(res.data.stats);
          }
        }
      } catch (err) {
        console.error("Failed to fetch system logs:", err);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [token, pagination.currentPage, searchTerm, selectedLevel, selectedCategory]
  );

  useEffect(() => {
    fetchLogs({ page: 1 });
  }, [fetchLogs]);

  // Auto-refresh interval handler
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshTimerRef.current = setInterval(() => {
        fetchLogs({ isBackground: true });
      }, 5000);
    } else {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
    }
    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
    };
  }, [autoRefresh, fetchLogs]);

  // Form search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(localSearch);
    fetchLogs({ page: 1, search: localSearch });
  };

  const handleClearSearch = () => {
    setLocalSearch("");
    setSearchTerm("");
    fetchLogs({ page: 1, search: "" });
  };

  const handleResetFilters = () => {
    setLocalSearch("");
    setSearchTerm("");
    setSelectedLevel("all");
    setSelectedCategory("all");
    fetchLogs({ page: 1, search: "", level: "all", category: "all" });
  };

  // Clear all logs
  const handleClearAllLogs = async () => {
    if (
      !window.confirm(
        "Are you sure you want to clear all system audit and error logs? This cannot be undone."
      )
    ) {
      return;
    }
    try {
      setRefreshing(true);
      await axios.delete(`${process.env.REACT_APP_API_BASE}/student/system-logs/clear`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setActionFeedback({
        type: "success",
        text: "All system logs cleared successfully!",
      });
      fetchLogs({ page: 1 });
    } catch (err) {
      setActionFeedback({
        type: "danger",
        text: err?.response?.data?.message || "Failed to clear logs",
      });
    } finally {
      setRefreshing(false);
    }
  };

  // Trigger test log
  const handleSimulateTestLog = async (level) => {
    try {
      setRefreshing(true);
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/student/system-logs/test`,
        {
          level,
          category: "system",
          message: `Simulated test ${level.toUpperCase()} log event from Admin Dashboard`,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setActionFeedback({
        type: "success",
        text: res.data?.message || `Simulated ${level} log event created!`,
      });
      fetchLogs({ page: 1 });
    } catch (err) {
      setActionFeedback({
        type: "danger",
        text: "Failed to simulate test log",
      });
    } finally {
      setRefreshing(false);
    }
  };

  // Export logs to CSV
  const handleExportCSV = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/student/system-logs?limit=all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.data && res.data.data) {
        const rows = res.data.data.map((l) => [
          `"${l.createdAt || ""}"`,
          `"${(l.level || "").toUpperCase()}"`,
          `"${(l.category || "").toUpperCase()}"`,
          `"${l.action || ""}"`,
          `"${(l.message || "").replace(/"/g, '""')}"`,
          `"${l.ipAddress || ""}"`,
          `"${l.user?.name || l.user?.reg_no || ""}"`,
        ]);
        const headers = ["Timestamp", "Level", "Category", "Action", "Message", "IP Address", "User"];
        const csvContent =
          "data:text/csv;charset=utf-8," +
          [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `system_logs_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      alert("Failed to export logs");
    }
  };

  // Copy JSON details to clipboard
  const handleCopyDetails = (id, details) => {
    navigator.clipboard.writeText(JSON.stringify(details, null, 2));
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Format relative date
  const formatTime = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    return date.toLocaleString("en-IN", {
      dateStyle: "short",
      timeStyle: "medium",
    });
  };

  // Helper for pagination numbers
  const getPaginationPages = () => {
    const totalPages = pagination.totalPages || 1;
    const currentPage = pagination.currentPage || 1;
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (currentPage >= totalPages - 1) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", currentPage, "...", totalPages);
      }
    }
    return pages;
  };

  const getLevelBadge = (level) => {
    switch (level) {
      case "error":
        return (
          <span
            style={{
              backgroundColor: "#fef2f2",
              color: "#dc2626",
              border: "1px solid #fecaca",
              padding: "2px 8px",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              textTransform: "uppercase",
            }}
          >
            <FiXCircle size={12} /> ERROR
          </span>
        );
      case "warn":
        return (
          <span
            style={{
              backgroundColor: "#fffbeb",
              color: "#d97706",
              border: "1px solid #fde68a",
              padding: "2px 8px",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              textTransform: "uppercase",
            }}
          >
            <FiAlertTriangle size={12} /> WARN
          </span>
        );
      case "success":
        return (
          <span
            style={{
              backgroundColor: "#ecfdf5",
              color: "#059669",
              border: "1px solid #a7f3d0",
              padding: "2px 8px",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              textTransform: "uppercase",
            }}
          >
            <FiCheckCircle size={12} /> SUCCESS
          </span>
        );
      default:
        return (
          <span
            style={{
              backgroundColor: "#eff6ff",
              color: "#2563eb",
              border: "1px solid #bfdbfe",
              padding: "2px 8px",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              textTransform: "uppercase",
            }}
          >
            <FiInfo size={12} /> INFO
          </span>
        );
    }
  };

  const isFiltered =
    searchTerm !== "" || selectedLevel !== "all" || selectedCategory !== "all";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Feedback banner */}
      {actionFeedback && (
        <div
          style={{
            padding: "12px 18px",
            borderRadius: "8px",
            backgroundColor:
              actionFeedback.type === "success" ? "#ecfdf5" : "#fef2f2",
            color: actionFeedback.type === "success" ? "#065f46" : "#991b1b",
            border:
              actionFeedback.type === "success"
                ? "1px solid #a7f3d0"
                : "1px solid #fecaca",
            fontSize: "13.5px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>{actionFeedback.text}</span>
          <button
            type="button"
            onClick={() => setActionFeedback(null)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "inherit" }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Stats Summary Cards */}
      <div className="admin-stats-grid">
        {/* Total Logs */}
        <div className="admin-stat-card">
          <div className="admin-stat-title">Total Logs</div>
          <div className="admin-stat-value">{stats.totalLogs || 0}</div>
        </div>

        {/* Errors */}
        <div className="admin-stat-card">
          <div className="admin-stat-title">Errors</div>
          <div className="admin-stat-value" style={{ color: stats.errors > 0 ? "#dc2626" : "#0f172a" }}>
            {stats.errors || 0}
          </div>
        </div>

        {/* Warnings */}
        <div className="admin-stat-card">
          <div className="admin-stat-title">Warnings</div>
          <div className="admin-stat-value" style={{ color: stats.warnings > 0 ? "#d97706" : "#0f172a" }}>
            {stats.warnings || 0}
          </div>
        </div>

        {/* Success */}
        <div className="admin-stat-card">
          <div className="admin-stat-title">Success Events</div>
          <div className="admin-stat-value" style={{ color: "#059669" }}>
            {stats.success || 0}
          </div>
        </div>

        {/* Info */}
        <div className="admin-stat-card">
          <div className="admin-stat-title">Info Logs</div>
          <div className="admin-stat-value" style={{ color: "#2563eb" }}>
            {stats.info || 0}
          </div>
        </div>
      </div>

      {/* Main Container Card */}
      <div className="admin-card">
        {/* Top Header Controls */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "14px",
            marginBottom: "18px",
            borderBottom: "1px solid #f1f5f9",
            paddingBottom: "14px",
          }}
        >
          <div>
            <h4
              style={{
                fontSize: "17px",
                fontWeight: 700,
                color: "#0f172a",
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FiTerminal color="#ea580c" /> System & Audit Activity Logs
            </h4>
            <div style={{ fontSize: "12.5px", color: "#64748b", marginTop: "2px" }}>
              Live server events, user actions, error stack traces, and database operations.
            </div>
          </div>

          {/* Actions & Live Toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            {/* Auto Refresh Toggle */}
            <button
              type="button"
              onClick={() => setAutoRefresh((prev) => !prev)}
              style={{
                height: "36px",
                padding: "0 12px",
                borderRadius: "8px",
                border: autoRefresh ? "1px solid #6ee7b7" : "1px solid #cbd5e1",
                backgroundColor: autoRefresh ? "#ecfdf5" : "#f8fafc",
                color: autoRefresh ? "#065f46" : "#475569",
                fontSize: "12.5px",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              title="Automatically refresh log stream every 5 seconds"
            >
              {autoRefresh ? (
                <>
                  <span
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: "#10b981",
                      display: "inline-block",
                      boxShadow: "0 0 8px rgba(16,185,129,0.8)",
                    }}
                  />
                  Live (5s)
                </>
              ) : (
                <>
                  <FiPlay size={12} /> Auto-Refresh: OFF
                </>
              )}
            </button>

            {/* Manual Refresh */}
            <button
              type="button"
              onClick={() => fetchLogs()}
              disabled={refreshing}
              style={{
                height: "36px",
                padding: "0 12px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                backgroundColor: "#fff",
                color: "#334155",
                fontSize: "12.5px",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                cursor: refreshing ? "not-allowed" : "pointer",
              }}
              title="Refresh log list"
            >
              <FiRefreshCw size={13} style={{ animation: refreshing ? "spin 1s linear infinite" : "none" }} />
              Refresh
            </button>

            {/* Test Simulation Buttons */}
            <div style={{ display: "inline-flex", gap: "4px" }}>
              <button
                type="button"
                onClick={() => handleSimulateTestLog("error")}
                style={{
                  height: "36px",
                  padding: "0 10px",
                  borderRadius: "8px",
                  border: "1px solid #fecaca",
                  backgroundColor: "#fef2f2",
                  color: "#dc2626",
                  fontSize: "12px",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                }}
                title="Simulate a test ERROR log event"
              >
                <FiZap size={12} /> Test Error
              </button>
              <button
                type="button"
                onClick={() => handleSimulateTestLog("success")}
                style={{
                  height: "36px",
                  padding: "0 10px",
                  borderRadius: "8px",
                  border: "1px solid #a7f3d0",
                  backgroundColor: "#ecfdf5",
                  color: "#059669",
                  fontSize: "12px",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                }}
                title="Simulate a test SUCCESS log event"
              >
                <FiCheckCircle size={12} /> Test Success
              </button>
            </div>

            {/* Export CSV */}
            <button
              type="button"
              onClick={handleExportCSV}
              style={{
                height: "36px",
                padding: "0 12px",
                borderRadius: "8px",
                border: "1px solid #0f172a",
                backgroundColor: "#0f172a",
                color: "#fff",
                fontSize: "12.5px",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                cursor: "pointer",
              }}
              title="Download all logs as CSV"
            >
              <FiDownload size={13} /> Export CSV
            </button>

            {/* Clear All Logs */}
            <button
              type="button"
              onClick={handleClearAllLogs}
              style={{
                height: "36px",
                padding: "0 10px",
                borderRadius: "8px",
                border: "1px solid #fecaca",
                backgroundColor: "#fef2f2",
                color: "#dc2626",
                fontSize: "12.5px",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
              }}
              title="Clear all system logs"
            >
              <FiTrash2 size={13} /> Clear
            </button>
          </div>
        </div>

        {/* Search & Filters Toolbar */}
        <div className="admin-toolbar">
          {/* Search Form */}
          <form onSubmit={handleSearchSubmit} className="admin-search-form">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                height: "38px",
                border: "1px solid #cbd5e1",
                borderRadius: "8px",
                padding: "0 10px",
                backgroundColor: "#f8fafc",
                boxSizing: "border-box",
              }}
            >
              <FiSearch color="#94a3b8" size={15} style={{ flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search message, action, user, IP..."
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0 8px",
                  border: "none",
                  outline: "none",
                  backgroundColor: "transparent",
                  fontSize: "13px",
                  color: "#1e293b",
                }}
              />
              {localSearch && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#94a3b8",
                    cursor: "pointer",
                    padding: "2px",
                    display: "flex",
                    alignItems: "center",
                  }}
                  title="Clear Search"
                >
                  <FiX />
                </button>
              )}
            </div>
            <button
              type="submit"
              style={{
                height: "38px",
                padding: "0 14px",
                backgroundColor: "#e06d15",
                color: "#fff",
                fontWeight: 600,
                fontSize: "13px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
                boxSizing: "border-box",
              }}
            >
              <FiSearch size={13} /> Search
            </button>
          </form>

          {/* Filters Group */}
          <div className="admin-filters-group">
            {/* Level Filter */}
            <select
              value={selectedLevel}
              onChange={(e) => {
                setSelectedLevel(e.target.value);
                fetchLogs({ page: 1, level: e.target.value });
              }}
              style={{
                height: "38px",
                padding: "0 10px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                backgroundColor: "#f8fafc",
                fontWeight: 500,
                color: "#334155",
                cursor: "pointer",
                outline: "none",
              }}
            >
              <option value="all">All Levels</option>
              <option value="error">Error Only</option>
              <option value="warn">Warnings Only</option>
              <option value="success">Success Only</option>
              <option value="info">Info Only</option>
            </select>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                fetchLogs({ page: 1, category: e.target.value });
              }}
              style={{
                height: "38px",
                padding: "0 10px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                backgroundColor: "#f8fafc",
                fontWeight: 500,
                color: "#334155",
                cursor: "pointer",
                outline: "none",
              }}
            >
              <option value="all">All Categories</option>
              <option value="auth">Auth & Security</option>
              <option value="student">Student Operations</option>
              <option value="due">Dues & Penalties</option>
              <option value="email">Email Service</option>
              <option value="department">Department</option>
              <option value="system">System & Database</option>
            </select>

            {/* Reset Filter Button */}
            {isFiltered && (
              <button
                type="button"
                onClick={handleResetFilters}
                className="admin-btn-reset"
                style={{
                  height: "38px",
                  padding: "0 12px",
                  backgroundColor: "#f1f5f9",
                  color: "#64748b",
                  fontWeight: 600,
                  fontSize: "13px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                title="Reset all search and filter values"
              >
                <FiRotateCcw size={13} /> Reset
              </button>
            )}
          </div>
        </div>

        {/* Log Entries Table Section */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spinner animation="border" variant="warning" />
            <p style={{ marginTop: "12px", color: "#64748b", fontSize: "14px" }}>
              Loading system logs...
            </p>
          </div>
        ) : logs.length === 0 ? (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              color: "#64748b",
              background: "#f8fafc",
              borderRadius: "10px",
              border: "1px dashed #cbd5e1",
            }}
          >
            <FiTerminal size={32} color="#94a3b8" style={{ marginBottom: "8px" }} />
            <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
              No system logs found
            </div>
            <div style={{ fontSize: "13px", color: "#64748b", marginTop: "4px" }}>
              {isFiltered
                ? "No log entries match your active filters. Try resetting."
                : "No logs recorded in the system yet."}
            </div>
          </div>
        ) : (
          <>
            <div className="admin-table-wrapper">
              <table className="admin-table" style={{ fontSize: "13px" }}>
                <thead>
                  <tr
                    style={{
                      textAlign: "left",
                      borderBottom: "2px solid #e2e8f0",
                      color: "#475569",
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <th style={{ padding: "12px 14px", width: "95px" }}>Level</th>
                    <th style={{ padding: "12px 14px", width: "150px" }}>Timestamp</th>
                    <th style={{ padding: "12px 14px", width: "140px" }}>Action</th>
                    <th style={{ padding: "12px 14px" }}>Message / Event</th>
                    <th style={{ padding: "12px 14px", width: "140px" }}>User / Source</th>
                    <th style={{ padding: "12px 14px", textAlign: "right", width: "80px" }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const isExpanded = expandedId === log._id;
                    return (
                      <React.Fragment key={log._id}>
                        <tr
                          style={{
                            borderBottom: isExpanded ? "none" : "1px solid #f1f5f9",
                            backgroundColor: isExpanded
                              ? "#f8fafc"
                              : log.level === "error"
                              ? "#fff5f5"
                              : "transparent",
                            cursor: "pointer",
                            transition: "background 0.15s ease",
                          }}
                          onClick={() => setExpandedId(isExpanded ? null : log._id)}
                        >
                          {/* Level */}
                          <td style={{ padding: "12px 14px" }}>{getLevelBadge(log.level)}</td>

                          {/* Timestamp */}
                          <td style={{ padding: "12px 14px", color: "#64748b", fontSize: "12px" }}>
                            {formatTime(log.createdAt)}
                          </td>

                          {/* Action & Category */}
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ fontWeight: 600, color: "#1e293b", fontSize: "12.5px" }}>
                              {log.action}
                            </div>
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#94a3b8",
                                textTransform: "uppercase",
                                letterSpacing: "0.3px",
                              }}
                            >
                              {log.category}
                            </div>
                          </td>

                          {/* Message */}
                          <td style={{ padding: "12px 14px" }}>
                            <div
                              style={{
                                color: log.level === "error" ? "#b91c1c" : "#334155",
                                fontWeight: log.level === "error" ? 600 : 500,
                                lineHeight: 1.4,
                              }}
                            >
                              {log.message}
                            </div>
                            {log.path && (
                              <div
                                style={{
                                  fontSize: "11px",
                                  fontFamily: "monospace",
                                  color: "#64748b",
                                  marginTop: "2px",
                                }}
                              >
                                {log.method} {log.path} {log.statusCode ? `(${log.statusCode})` : ""}
                              </div>
                            )}
                          </td>

                          {/* User / IP */}
                          <td style={{ padding: "12px 14px", fontSize: "12px", color: "#475569" }}>
                            {log.user ? (
                              <div>
                                <div style={{ fontWeight: 600, color: "#1e293b" }}>
                                  {log.user.name || log.user.reg_no || "User"}
                                </div>
                                <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                                  {log.user.role || "role"}
                                </div>
                              </div>
                            ) : (
                              <div>{log.ipAddress || "System"}</div>
                            )}
                          </td>

                          {/* Expand Trigger */}
                          <td style={{ padding: "12px 14px", textAlign: "right" }}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedId(isExpanded ? null : log._id);
                              }}
                              style={{
                                background: "none",
                                border: "1px solid #cbd5e1",
                                borderRadius: "6px",
                                padding: "4px 8px",
                                color: "#475569",
                                cursor: "pointer",
                                fontSize: "12px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                              title="Inspect JSON details & stack trace"
                            >
                              {isExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                            </button>
                          </td>
                        </tr>

                        {/* Expanded JSON Inspector Row */}
                        {isExpanded && (
                          <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                            <td colSpan="6" style={{ padding: "0 14px 14px 14px" }}>
                              <div
                                style={{
                                  backgroundColor: "#0f172a",
                                  color: "#f8fafc",
                                  borderRadius: "8px",
                                  padding: "14px",
                                  fontFamily: "monospace",
                                  fontSize: "12px",
                                  position: "relative",
                                  maxHeight: "300px",
                                  overflowY: "auto",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "8px",
                                    borderBottom: "1px solid #334155",
                                    paddingBottom: "6px",
                                  }}
                                >
                                  <span style={{ color: "#94a3b8", fontSize: "11px" }}>
                                    Log ID: {log._id} • {log.action}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleCopyDetails(log._id, log)}
                                    style={{
                                      background: "#1e293b",
                                      border: "1px solid #475569",
                                      color: "#cbd5e1",
                                      padding: "3px 8px",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      cursor: "pointer",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    {copiedId === log._id ? (
                                      <>
                                        <FiCheck color="#10b981" /> Copied!
                                      </>
                                    ) : (
                                      <>
                                        <FiCopy /> Copy JSON
                                      </>
                                    )}
                                  </button>
                                </div>
                                <pre
                                  style={{
                                    margin: 0,
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-all",
                                    color: "#38bdf8",
                                  }}
                                >
                                  {JSON.stringify(
                                    {
                                      id: log._id,
                                      level: log.level,
                                      category: log.category,
                                      action: log.action,
                                      message: log.message,
                                      details: log.details,
                                      user: log.user,
                                      ipAddress: log.ipAddress,
                                      path: log.path,
                                      method: log.method,
                                      statusCode: log.statusCode,
                                      timestamp: log.createdAt,
                                    },
                                    null,
                                    2
                                  )}
                                </pre>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            <div className="admin-pagination-wrapper">
              <div className="admin-pagination-info">
                <span>Showing</span>
                <span
                  style={{
                    fontWeight: 700,
                    color: "#0f172a",
                    backgroundColor: "#f8fafc",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  {(pagination.currentPage - 1) * pagination.limit + 1} –{" "}
                  {Math.min(pagination.currentPage * pagination.limit, pagination.total)}
                </span>
                <span>of</span>
                <span style={{ fontWeight: 700, color: "#0f172a" }}>
                  {pagination.total}
                </span>
                <span>logs</span>
              </div>

              {/* Controls */}
              <div className="admin-pagination-controls">
                <button
                  type="button"
                  className="admin-page-jump-btn"
                  onClick={() => fetchLogs({ page: 1 })}
                  disabled={pagination.currentPage === 1}
                  style={{
                    height: "36px",
                    width: "36px",
                    padding: 0,
                    borderRadius: "8px",
                    border: pagination.currentPage === 1 ? "1px solid #f1f5f9" : "1px solid #e2e8f0",
                    backgroundColor: pagination.currentPage === 1 ? "#f8fafc" : "#ffffff",
                    color: pagination.currentPage === 1 ? "#cbd5e1" : "#475569",
                    cursor: pagination.currentPage === 1 ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="First Page"
                >
                  <FiChevronsLeft size={16} />
                </button>

                <button
                  type="button"
                  className="admin-page-nav-btn"
                  onClick={() => fetchLogs({ page: Math.max(1, pagination.currentPage - 1) })}
                  disabled={pagination.currentPage === 1}
                  style={{
                    height: "36px",
                    padding: "0 12px",
                    borderRadius: "8px",
                    border: pagination.currentPage === 1 ? "1px solid #f1f5f9" : "1px solid #e2e8f0",
                    backgroundColor: pagination.currentPage === 1 ? "#f8fafc" : "#ffffff",
                    color: pagination.currentPage === 1 ? "#cbd5e1" : "#475569",
                    cursor: pagination.currentPage === 1 ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                  title="Previous Page"
                >
                  <FiChevronLeft size={16} /> Prev
                </button>

                {getPaginationPages().map((page, idx) => {
                  if (page === "...") {
                    return (
                      <span
                        key={`ellipsis-${idx}`}
                        style={{
                          height: "34px",
                          minWidth: "22px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#94a3b8",
                          fontSize: "13px",
                          fontWeight: 600,
                        }}
                      >
                        ···
                      </span>
                    );
                  }
                  const isActive = pagination.currentPage === page;
                  return (
                    <button
                      key={`page-${page}`}
                      type="button"
                      className="admin-page-num-btn"
                      onClick={() => fetchLogs({ page })}
                      style={{
                        height: "36px",
                        minWidth: "36px",
                        padding: "0 8px",
                        borderRadius: "8px",
                        border: isActive ? "1px solid #ea580c" : "1px solid #e2e8f0",
                        background: isActive
                          ? "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)"
                          : "#ffffff",
                        color: isActive ? "#ffffff" : "#334155",
                        fontWeight: isActive ? 700 : 500,
                        cursor: "pointer",
                        fontSize: "13.5px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: isActive ? "0 2px 6px rgba(234, 88, 12, 0.35)" : "none",
                      }}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  type="button"
                  className="admin-page-nav-btn"
                  onClick={() =>
                    fetchLogs({ page: Math.min(pagination.totalPages, pagination.currentPage + 1) })
                  }
                  disabled={pagination.currentPage === pagination.totalPages}
                  style={{
                    height: "36px",
                    padding: "0 12px",
                    borderRadius: "8px",
                    border:
                      pagination.currentPage === pagination.totalPages
                        ? "1px solid #f1f5f9"
                        : "1px solid #e2e8f0",
                    backgroundColor:
                      pagination.currentPage === pagination.totalPages ? "#f8fafc" : "#ffffff",
                    color:
                      pagination.currentPage === pagination.totalPages ? "#cbd5e1" : "#475569",
                    cursor:
                      pagination.currentPage === pagination.totalPages
                        ? "not-allowed"
                        : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                  title="Next Page"
                >
                  Next <FiChevronRight size={16} />
                </button>

                <button
                  type="button"
                  className="admin-page-jump-btn"
                  onClick={() => fetchLogs({ page: pagination.totalPages })}
                  disabled={pagination.currentPage === pagination.totalPages}
                  style={{
                    height: "36px",
                    width: "36px",
                    padding: 0,
                    borderRadius: "8px",
                    border:
                      pagination.currentPage === pagination.totalPages
                        ? "1px solid #f1f5f9"
                        : "1px solid #e2e8f0",
                    backgroundColor:
                      pagination.currentPage === pagination.totalPages ? "#f8fafc" : "#ffffff",
                    color:
                      pagination.currentPage === pagination.totalPages ? "#cbd5e1" : "#475569",
                    cursor:
                      pagination.currentPage === pagination.totalPages
                        ? "not-allowed"
                        : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Last Page"
                >
                  <FiChevronsRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SystemLogs;
